import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, MailCheck } from "lucide-react";
import { api } from "../lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-16 flex items-center justify-center px-5 bg-[#0F0F10]">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md border border-white/10 bg-[#1F1F1F] p-8 sm:p-10"
        data-testid="forgot-password-page"
      >
        <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#4ADE80] mb-4">Account</p>
        <h1 className="font-display font-black text-5xl tracking-tighter uppercase leading-none">Reset</h1>

        {sent ? (
          <div data-testid="forgot-success" className="mt-8">
            <div className="flex items-center gap-3 border border-[#4ADE80]/40 bg-[#4ADE80]/10 px-4 py-4">
              <MailCheck className="h-5 w-5 text-[#4ADE80] shrink-0" />
              <p className="text-[#F5F5F5] text-sm leading-relaxed">If an account exists for <span className="text-[#4ADE80]">{email}</span>, a reset link is on its way. Check your inbox.</p>
            </div>
            <Link to="/login" className="mt-8 inline-block font-mono-accent text-xs text-[#4ADE80] link-underline">← Back to sign in</Link>
          </div>
        ) : (
          <>
            <p className="mt-4 text-[#A8A8A8] text-sm">Enter your email and we'll send you a link to reset your password.</p>
            {error && (
              <p data-testid="forgot-error" className="mt-6 border border-[#EF4444]/40 bg-[#EF4444]/10 text-[#EF4444] font-mono-accent text-xs px-4 py-3">{error}</p>
            )}
            <form onSubmit={submit} className="mt-8 space-y-4">
              <input data-testid="forgot-email" required type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-[#0F0F10] border border-white/10 px-4 py-4 font-mono-accent text-sm outline-none focus:border-[#4ADE80] transition-colors placeholder:text-[#7a7a7a]" />
              <button data-testid="forgot-submit" type="submit" disabled={loading} className="btn-press w-full bg-[#4ADE80] text-[#0F0F10] font-medium py-4 hover:bg-[#6FE998] hover:shadow-lg hover:shadow-[#4ADE80]/20 transition-[background-color,box-shadow] flex items-center justify-center gap-2 disabled:opacity-50">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {loading ? "Sending…" : "Send Reset Link"}
              </button>
            </form>
            <p className="mt-8 font-mono-accent text-xs text-[#7a7a7a] text-center">
              Remembered it?{" "}
              <Link to="/login" data-testid="back-to-login" className="text-[#4ADE80] link-underline">Sign in</Link>
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
}
